/**
 * As datas na tela, sob as preferências da pessoa — e a fronteira entre as duas coisas
 * que o produto chama de "data".
 *
 * ## Por que duas funções, e não uma
 *
 * **Data nominal** (vencimento, competência, emissão, prazo, aniversário) NÃO é um
 * instante. "Vence em 20/07" vence em 20/07 em São Paulo, em Bogotá e em Nova York — a
 * spec 01 §8.2 escreve isso em letra, e o domínio inteiro é construído sobre ela
 * (`packages/domain/src/data/`, que só usa `Date` numa porta). Passar `2026-07-20` por
 * `new Date()` e formatar entrega 19/07 para quem está a oeste, e é assim que um
 * vencimento muda de dia sozinho.
 *
 * **Instante** (histórico, sessão, auditoria, "atualizado às") É um ponto no tempo, e o
 * fuso escolhido é justamente o que decide em que dia e em que hora ele aconteceu para
 * quem lê. Este é o único lugar onde `fusoHorario` deve mexer no resultado.
 *
 * Uma função só para as duas coisas obrigaria cada chamador a lembrar da diferença, e
 * quem esquece não recebe erro — recebe um dia a menos, uma vez a cada vinte e quatro
 * horas de fuso.
 *
 * ## O que estas funções NÃO fazem
 *
 * Não tocam em payload, ordenação, filtro nem cálculo. O que viaja e o que se compara
 * continua sendo `YYYY-MM-DD` e o instante ISO; o formato é da BORDA, aplicado uma vez,
 * na hora de escrever na tela. E o relógio de NEGÓCIO (o `fn_hoje()` do banco, o "hoje"
 * das telas de campanha) é de São Paulo por decisão, não por preferência: ele não passa
 * por aqui.
 */
import type { PreferenciasGerais } from '@dv/contracts'
import { ehDataNominal } from '@dv/domain'

export type FormatoDeData = PreferenciasGerais['formatoDeData']

/** O fuso do NEGÓCIO — o mesmo do `fn_hoje()`. Vale quando a preferência não serve. */
export const FUSO_PADRAO = 'America/Sao_Paulo'

const DATA_NOMINAL = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * `YYYY-MM-DD` no formato escolhido, sem passar por `Date`.
 *
 * Recorte de string de propósito: é a mesma decisão do domínio. Uma data nominal que
 * atravessasse `Date` ganharia um fuso que ela não tem, e voltaria um dia atrás para
 * metade do continente.
 *
 * Devolve `null` — e não um palpite — quando a entrada não é uma data nominal. Quem
 * chama já tem a microcopy dele para o caso vazio, e inventar "01/01/1970" aqui seria
 * pôr na tela um dia que não existe em lugar nenhum.
 */
export function formatarDataNominal(
  data: string, formato: FormatoDeData, opcoes: { compacta?: boolean } = {},
): string | null {
  if (!ehDataNominal(data)) return null
  const partes = DATA_NOMINAL.exec(data)
  if (partes === null) return null
  const [, ano, mes, dia] = partes
  if (ano === undefined || mes === undefined || dia === undefined) return null
  // Regra aprovada para compactas sem exceção local: mantém ordem e separador
  // do formato escolhido. O chamador fornece o ano no detalhe acessível.
  if (opcoes.compacta === true) {
    if (formato === 'AAAA-MM-DD') return `${mes}-${dia}`
    return formato === 'MM/DD/AAAA' ? `${mes}/${dia}` : `${dia}/${mes}`
  }
  if (formato === 'AAAA-MM-DD') return `${ano}-${mes}-${dia}`
  if (formato === 'MM/DD/AAAA') return `${mes}/${dia}/${ano}`
  return `${dia}/${mes}/${ano}`
}

interface PartesDoInstante {
  readonly ano: string
  readonly mes: string
  readonly dia: string
  readonly hora: string
  readonly minuto: string
}

/**
 * As partes de um instante NO FUSO pedido.
 *
 * `formatToParts` em vez de `format`: o texto de `format` muda com o idioma e com a
 * versão do ICU, e montar o formato pedido a partir de um texto localizado seria
 * adivinhar onde o dia está. As partes vêm nomeadas.
 *
 * `hourCycle: 'h23'` explícito porque `hour12: false` sozinho ainda devolve "24" para a
 * meia-noite em parte das versões do ICU — e "24:10" não é hora nenhuma.
 *
 * Fuso inválido não derruba a tela: a preferência é `z.string()` no contrato, então um
 * valor que o `Intl` recusa cai no fuso do negócio. Perder o fuso escolhido é ruim;
 * perder a tela inteira por causa dele é pior.
 */
function partesNoFuso(iso: string, fuso: string): PartesDoInstante | null {
  // PostgreSQL OF pode emitir +00 ou -03. Completar os minutos preserva o
  // deslocamento conhecido; não atribuímos fuso a um horário ingênuo.
  if (!/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}(?::?\d{2})?)$/.test(iso)) return null
  if (!ehDataNominal(iso.slice(0, 10))) return null
  const comSeparadorIso = iso.replace(' ', 'T')
  const normalizado = /[+-]\d{2}$/.test(comSeparadorIso) ? `${comSeparadorIso}:00` : comSeparadorIso
  const instante = new Date(normalizado)
  if (Number.isNaN(instante.getTime())) return null
  const montar = (zona: string): Intl.DateTimeFormat => new Intl.DateTimeFormat('en-CA', {
    timeZone: zona,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  })
  let formatador: Intl.DateTimeFormat
  try {
    formatador = montar(fuso)
  } catch {
    formatador = montar(FUSO_PADRAO)
  }
  const partes = new Map(formatador.formatToParts(instante).map((p) => [p.type, p.value]))
  const ano = partes.get('year')
  const mes = partes.get('month')
  const dia = partes.get('day')
  const hora = partes.get('hour')
  const minuto = partes.get('minute')
  if (ano === undefined || mes === undefined || dia === undefined
    || hora === undefined || minuto === undefined) return null
  return { ano, mes, dia, hora, minuto }
}

/**
 * A data NOMINAL em que um instante caiu, no fuso escolhido — `YYYY-MM-DD`.
 *
 * É a ponte entre os dois mundos, e a única: quem precisa agrupar notificações por dia
 * ou dizer "ontem" pergunta aqui, uma vez, e volta a trabalhar com data nominal.
 */
export function dataNominalDoInstante(iso: string, fuso: string): string | null {
  const partes = partesNoFuso(iso, fuso)
  return partes === null ? null : `${partes.ano}-${partes.mes}-${partes.dia}`
}

/**
 * Um instante ISO na tela: no FUSO escolhido e no FORMATO escolhido.
 *
 * `hora` liga o `HH:mm` — o padrão é só a data, porque a maioria das telas mostra o dia
 * e não o minuto.
 */
export function formatarInstante(
  iso: string, fuso: string, formato: FormatoDeData, opcoes: { hora?: boolean; compacta?: boolean } = {},
): string | null {
  const partes = partesNoFuso(iso, fuso)
  if (partes === null) return null
  const data = formatarDataNominal(`${partes.ano}-${partes.mes}-${partes.dia}`, formato, opcoes)
  if (data === null) return null
  return opcoes.hora === true ? `${data} ${partes.hora}:${partes.minuto}` : data
}

/** Só o relógio de um instante no fuso escolhido — `HH:mm`. */
export function horaDoInstante(iso: string, fuso: string): string | null {
  const partes = partesNoFuso(iso, fuso)
  return partes === null ? null : `${partes.hora}:${partes.minuto}`
}

/**
 * A IDADE de algo, numa grafia só para o produto inteiro (D-13, D-14): `agora`, `há 1 min`, `há 6 min`,
 * `há 1 hora`, `há 3 horas`, `há 1 dia`, `há 4 dias`; e `hoje` quando a contagem é em dias e ainda é zero.
 *
 * Havia quatro (`há 6 min`, `há 3d`, `há 3 dias`, `há 1 dias`), uma em cada tela. Recebe a duração já
 * medida, em minutos (de um instante: `minutosDesde`) ou em dias (o aging que a API conta); nunca lê o
 * relógio. Negativo conta como zero: o futuro não tem idade.
 */
export function haQuanto(duracao: { readonly minutos: number } | { readonly dias: number }): string {
  if ('dias' in duracao) {
    const dias = Math.max(0, Math.round(duracao.dias))
    if (dias === 0) return 'hoje'
    return dias === 1 ? 'há 1 dia' : `há ${String(dias)} dias`
  }
  const minutos = Math.max(0, Math.round(duracao.minutos))
  if (minutos === 0) return 'agora'
  if (minutos < 60) return `há ${String(minutos)} min`
  const horas = Math.round(minutos / 60)
  if (horas < 24) return horas === 1 ? 'há 1 hora' : `há ${String(horas)} horas`
  return haQuanto({ dias: minutos / 1440 })
}

/** Os minutos entre um instante ISO e `agoraMs`; `null` quando o texto não é instante. */
export function minutosDesde(iso: string, agoraMs: number): number | null {
  const instante = Date.parse(iso)
  return Number.isNaN(instante) ? null : (agoraMs - instante) / 60_000
}
